import { useId, useState } from "react";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Link,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import type { Promisable } from "type-fest";

import {
  FULL_FILTERS,
  itemsShown,
  type FullAssessmentView,
  type FullDomain,
  type FullFilter,
  type FullItem,
  type GroupCategory,
  type RatingShown,
} from "@thalamos/haven-common";

import { VISUALLY_HIDDEN } from "../../theme/visuallyHidden.js";
import { RatingBadge, useAnswerWords } from "../RatingBadge/RatingBadge.js";

/** How much of a comment shows before it is read in full. */
const COMMENT_PREVIEW = 240;

const OUTLINED = {
  textTransform: "none",
  fontWeight: 600,
  color: "text.primary",
  borderColor: "divider",
} as const;

/** Clamps long text to `lines` lines, unless the reader asked for it in full. */
const clamped = (lines: number, fullText: boolean) =>
  fullText
    ? {}
    : {
        display: "-webkit-box",
        WebkitLineClamp: lines,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
      };

const LINK_BUTTON = {
  p: 0,
  minWidth: 0,
  fontWeight: 700,
  textTransform: "none",
  "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
} as const;

/** The id an item's card carries, so an address can point at it. */
export const itemAnchorOf = ({
  group,
  item,
}: Pick<FullItem, "group" | "item">) => `item-${group}-${item}`;

const ratingOf = ({ value }: RatingShown) =>
  typeof value === "number" ? value : undefined;

const sameWords = ({
  left,
  right,
}: {
  left: string | undefined;
  right: string;
}) => left?.toLocaleLowerCase("en-GB") === right.toLocaleLowerCase("en-GB");

/** How many of a domain's items call for action, or that none does. */
const ActionCount = ({ count }: { count: number }) => {
  const { t } = useTranslation();

  return count === 0 ? (
    <Typography component="span" variant="caption" color="text.secondary">
      {t("assessment.full.domainNoActions")}
    </Typography>
  ) : (
    <Box
      component="span"
      sx={{
        px: 0.875,
        py: 0.125,
        borderRadius: 999,
        fontSize: "0.75rem",
        fontWeight: 500,
        bgcolor: (theme) => alpha(theme.palette.error.main, 0.12),
        color: "error.dark",
      }}
    >
      {t("assessment.full.domainActions", { count })}
    </Box>
  );
};

/** Text clamped to `lines` lines, with a link to read it in full unless full text was asked for. */
const AboutText = ({
  text,
  lines,
  fullText,
  about,
}: {
  text: string;
  lines: number;
  fullText: boolean;
  /** What the link to read it in full says. */
  about: string;
}) => {
  const { t } = useTranslation();
  const [reading, setReading] = useState(false);

  return (
    <>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 0.5, ...clamped(lines, fullText || reading) }}
      >
        {text}
      </Typography>
      {fullText ? null : (
        <Button
          size="small"
          aria-expanded={reading}
          onClick={() => {
            setReading(!reading);
          }}
          sx={{ ...LINK_BUTTON, mt: 0.25 }}
        >
          {reading ? t("assessment.full.showLess") : about}
        </Button>
      )}
    </>
  );
};

const RatingsOffered = ({
  item,
  category,
}: {
  item: FullItem;
  category: GroupCategory | undefined;
}) => {
  const { t } = useTranslation();

  return (
    <Stack
      component="ol"
      spacing={0.75}
      aria-label={t("assessment.full.ratingsOffered", { title: item.title })}
      sx={{ m: 0, p: 0, listStyle: "none" }}
    >
      {item.ratings.map((offered) => {
        const given = offered.value === item.rating?.value;

        return (
          <Stack
            key={String(offered.value)}
            component="li"
            direction="row"
            spacing={1}
            aria-current={given ? "true" : undefined}
            sx={{
              p: 0.75,
              borderRadius: 1,
              border: 1,
              borderColor: given ? "text.primary" : "transparent",
            }}
          >
            <RatingBadge
              category={category}
              rating={ratingOf(offered)}
              scale={item.scale}
              size="small"
            />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {offered.title}
                {given ? ` · ${t("assessment.full.given")}` : ""}
              </Typography>
              {offered.description === undefined ? null : (
                <Typography variant="caption" color="text.secondary">
                  {offered.description}
                </Typography>
              )}
            </Box>
          </Stack>
        );
      })}
    </Stack>
  );
};

/** What the assessor said of an item, or that they said nothing. */
const CommentShown = ({
  comment,
  fullText,
}: {
  comment: string | null;
  fullText: boolean;
}) => {
  const { t } = useTranslation();
  const [reading, setReading] = useState(false);
  const long = comment !== null && comment.length > COMMENT_PREVIEW;
  const whole = fullText || reading || !long;
  const Icon = comment === null ? ChatBubbleOutlineIcon : ChatBubbleIcon;

  return (
    <Stack direction="row" spacing={1.25} alignItems="flex-start">
      <Icon
        aria-hidden
        sx={{
          mt: 0.25,
          fontSize: 16,
          color: comment === null ? "text.disabled" : "text.secondary",
        }}
      />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" component="p" color="text.secondary">
          {t("assessment.full.comment")}
        </Typography>
        {comment === null ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: "italic" }}
          >
            {t("assessment.full.noComment")}
          </Typography>
        ) : (
          <>
            <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
              {whole
                ? comment
                : `${comment.slice(0, COMMENT_PREVIEW).trimEnd()}…`}
            </Typography>
            {fullText || !long ? null : (
              <Button
                size="small"
                onClick={() => {
                  setReading(!reading);
                }}
                sx={{ ...LINK_BUTTON, mt: 0.5 }}
              >
                {reading
                  ? t("assessment.full.readLess")
                  : t("assessment.full.readMore")}
              </Button>
            )}
          </>
        )}
      </Box>
    </Stack>
  );
};

/** How wide an item's number is set, so its title lines up whatever the number. */
const ITEM_NUMBER_WIDTH = 28;

/** The space between an item's number and its title. */
const ITEM_NUMBER_GAP = 10;

/** Lines an item's description and answer up beneath its title, past its number. */
const ITEM_INDENT = {
  xs: 0,
  sm: `${String(ITEM_NUMBER_WIDTH + ITEM_NUMBER_GAP)}px`,
} as const;

const ItemCard = ({
  item,
  category,
  fullText,
  historyHref,
  offersAllRatings,
}: {
  item: FullItem;
  category: GroupCategory | undefined;
  fullText: boolean;
  historyHref?: string | undefined;
  offersAllRatings: boolean;
}) => {
  const { t } = useTranslation();
  const wordOf = useAnswerWords();
  const rating = item.rating === undefined ? undefined : ratingOf(item.rating);
  const badgeWord =
    rating === undefined ? undefined : wordOf({ scale: item.scale, rating });
  const [showingRatings, setShowingRatings] = useState(false);
  const history =
    historyHref === undefined ? null : (
      <Link component={RouterLink} to={historyHref} variant="body2">
        {t("assessment.full.itemHistory")}
      </Link>
    );

  return (
    <Box
      id={itemAnchorOf(item)}
      component="li"
      sx={{ px: 2.5, py: 2.25, borderTop: 1, borderColor: "divider" }}
    >
      <Typography
        component="h4"
        sx={{
          m: 0,
          display: "flex",
          alignItems: "baseline",
          gap: `${String(ITEM_NUMBER_GAP)}px`,
          fontSize: "0.9375rem",
          fontWeight: 700,
          color: "primary.main",
        }}
      >
        <Box
          component="span"
          sx={{
            flex: "none",
            minWidth: ITEM_NUMBER_WIDTH,
            color: "text.secondary",
          }}
        >
          {item.number}
        </Box>{" "}
        <span>{item.title}</span>
      </Typography>
      {item.description === undefined ? null : (
        <Box sx={{ ml: ITEM_INDENT }}>
          <AboutText
            text={item.description}
            lines={5}
            fullText={fullText}
            about={t("assessment.full.aboutItem")}
          />
        </Box>
      )}
      <Box
        sx={{
          ml: ITEM_INDENT,
          mt: 1.25,
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            md: "minmax(0, 1fr) minmax(0, 1fr)",
          },
          bgcolor: "background.subtle",
          border: 1,
          borderColor: "divider",
          borderRadius: 1.5,
        }}
      >
        <Box sx={{ px: 1.75, py: 1.5 }}>
          {item.notAnswered ? (
            <>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  size="small"
                  label={t("assessment.full.notAnsweredMark")}
                  aria-hidden
                  sx={{ minWidth: 40, bgcolor: "grey.300", color: "grey.800" }}
                />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t("assessment.full.notAnswered")}
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ mt: 0.75 }}>
                {t("assessment.full.sentWithoutRating")}
              </Typography>
            </>
          ) : item.rating === undefined ? (
            <>
              <Typography variant="caption" color="text.secondary">
                {t("assessment.full.answer")}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {item.answer}
              </Typography>
            </>
          ) : (
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <RatingBadge
                category={category}
                rating={ratingOf(item.rating)}
                scale={item.scale}
                size="wide"
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="caption"
                  component="p"
                  sx={{ fontWeight: 600, color: "text.secondary" }}
                >
                  {t("assessment.full.ratingSelected")}
                </Typography>
                <Typography
                  // The badge, hidden from a screen reader, already says it.
                  sx={
                    sameWords({ left: badgeWord, right: item.rating.title })
                      ? VISUALLY_HIDDEN
                      : { fontSize: "1rem", fontWeight: 600, lineHeight: 1.35 }
                  }
                >
                  {item.rating.title}
                </Typography>
                {item.rating.description === undefined ? null : (
                  <Typography variant="body2" sx={{ mt: 0.375 }}>
                    {item.rating.description}
                  </Typography>
                )}
              </Box>
            </Stack>
          )}
          {!offersAllRatings || item.ratings.length === 0 ? null : (
            <Button
              size="small"
              aria-expanded={showingRatings}
              onClick={() => {
                setShowingRatings(!showingRatings);
              }}
              sx={{ ...LINK_BUTTON, mt: 0.75 }}
            >
              {showingRatings
                ? t("assessment.full.hideRatings")
                : t("assessment.full.showRatings")}
            </Button>
          )}
          {showingRatings ? (
            <Box sx={{ mt: 1 }}>
              <RatingsOffered item={item} category={category} />
            </Box>
          ) : null}
        </Box>
        {item.notAnswered ? null : (
          <Box
            sx={{
              px: 1.75,
              py: 1.5,
              // Widths rather than `borderTop`/`borderLeft`, whose shorthand
              // would reset the colour at each breakpoint.
              borderStyle: "solid",
              borderColor: "divider",
              borderWidth: { xs: "1px 0 0", md: "0 0 0 1px" },
            }}
          >
            <CommentShown comment={item.comment} fullText={fullText} />
          </Box>
        )}
      </Box>
      {history === null ? null : (
        <Box sx={{ ml: ITEM_INDENT, mt: 1 }}>{history}</Box>
      )}
    </Box>
  );
};

/**
 * One domain as a card: its heading and counts, over a line of what it
 * covers while closed, or all of what it covers and its items while open.
 */
const DomainCard = ({
  domain,
  filter,
  fullText,
  open,
  onToggle,
  historyHrefOf,
  offersAllRatings,
  categorised,
}: {
  domain: FullDomain;
  filter: FullFilter;
  fullText: boolean;
  open: boolean;
  onToggle: () => Promisable<void>;
  historyHrefOf?: ((item: FullItem) => string) | undefined;
  offersAllRatings: boolean;
  categorised: FullAssessmentView["categorised"];
}) => {
  const { t } = useTranslation();
  const itemsId = useId();
  const actions = domain.items.filter(({ action }) => action).length;

  return (
    <Paper
      variant="outlined"
      component="section"
      sx={{ borderRadius: 1.5, overflow: "hidden", borderColor: "divider" }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="flex-start"
        sx={{ minHeight: 64, px: 1, py: 1.25 }}
      >
        <IconButton
          size="small"
          aria-expanded={open}
          aria-controls={open ? itemsId : undefined}
          aria-label={`${open ? t("assessment.full.collapse") : t("assessment.full.expand")} ${domain.title}`}
          onClick={() => void onToggle()}
        >
          <ChevronRightIcon
            aria-hidden
            sx={{
              color: "text.secondary",
              transition: "transform 150ms",
              transform: open ? "rotate(90deg)" : "none",
            }}
          />
        </IconButton>
        <Box sx={{ minWidth: 0, flexGrow: 1, pt: 0.5 }}>
          <Stack
            direction="row"
            columnGap={1.5}
            rowGap={0.5}
            alignItems="baseline"
            useFlexGap
            flexWrap="wrap"
          >
            <Typography
              component="h3"
              sx={{ m: 0, fontSize: "1rem", fontWeight: 700 }}
            >
              <Box component="span" sx={{ color: "primary.main" }}>
                {domain.number}
              </Box>{" "}
              {domain.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("assessment.full.domainItems", { count: domain.items.length })}
            </Typography>
            {categorised ? <ActionCount count={actions} /> : null}
            {domain.notAnsweredCount === 0 ? null : (
              <Typography variant="body2" color="text.secondary">
                {domain.notAnsweredCount === domain.items.length
                  ? t("assessment.full.domainNoneAnswered")
                  : t("assessment.full.domainNotAnswered", {
                      count: domain.notAnsweredCount,
                    })}
              </Typography>
            )}
          </Stack>
          {open || domain.description === undefined ? null : (
            <AboutText
              text={domain.description}
              lines={1}
              fullText={fullText}
              about={t("assessment.full.aboutDomain")}
            />
          )}
        </Box>
      </Stack>
      {open ? (
        <Box id={itemsId} sx={{ borderTop: 1, borderColor: "divider" }}>
          {domain.description === undefined ? null : (
            <Typography
              variant="body2"
              sx={{ px: 2.5, py: 2, lineHeight: 1.6, bgcolor: "action.hover" }}
            >
              {domain.description}
            </Typography>
          )}
          <Box component="ol" sx={{ m: 0, p: 0, listStyle: "none" }}>
            {itemsShown({ domain, filter }).map((item) => (
              <ItemCard
                key={item.item}
                item={item}
                category={domain.category}
                fullText={fullText}
                offersAllRatings={offersAllRatings}
                historyHref={
                  item.inFullHistory ? historyHrefOf?.(item) : undefined
                }
              />
            ))}
          </Box>
        </Box>
      ) : null}
    </Paper>
  );
};

/**
 * Every answer of an assessment, domain by domain, each against the ratings
 * it might have been and with what the assessor said of it.
 *
 * Its filter, full text and open domains are handed in, so the page can keep
 * them in its address. A domain the filter leaves nothing of is left out.
 */
export const FullAssessment = ({
  view,
  filter,
  fullText,
  open,
  onFilter,
  onFullText,
  onOpen,
  historyHrefOf,
  offersAllRatings = true,
}: {
  view: FullAssessmentView;
  filter: FullFilter;
  fullText: boolean;
  /** The groups of the domains open. */
  open: ReadonlySet<string>;
  onFilter: (filter: FullFilter) => Promisable<void>;
  onFullText: (fullText: boolean) => Promisable<void>;
  /** Asks for exactly these domains to be open. */
  onOpen: (open: readonly string[]) => Promisable<void>;
  /** Where an item's history is, asked only of an item `inFullHistory`; absent where no history is offered. */
  historyHrefOf?: ((item: FullItem) => string) | undefined;
  /**
   * Whether each item offers every rating it might have been, beside the one
   * given; `false` shows the rating given alone.
   */
  offersAllRatings?: boolean | undefined;
}) => {
  const { t } = useTranslation();
  const domains = view.domains.filter(
    (domain) => itemsShown({ domain, filter }).length > 0,
  );
  const allOpen =
    domains.length > 0 && domains.every(({ group }) => open.has(group));
  // Only an assessment sent with items not answered offers to show them.
  const filters = FULL_FILTERS.filter(
    (each) =>
      (view.categorised || each !== "action") &&
      (view.notAnsweredCount > 0 || each !== "not_answered"),
  );
  const counts = [
    t("assessment.full.counts.items", { count: view.itemCount }),
    ...(view.notAnsweredCount === 0
      ? []
      : [
          t("assessment.full.counts.answered", {
            count: view.itemCount - view.notAnsweredCount,
          }),
          t("assessment.full.counts.notAnswered", {
            count: view.notAnsweredCount,
          }),
        ]),
    ...(view.categorised
      ? [t("assessment.full.counts.actions", { count: view.actionCount })]
      : []),
    t("assessment.full.counts.comments", { count: view.commentCount }),
  ];

  return (
    <Stack spacing={1.5}>
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        useFlexGap
        flexWrap="wrap"
      >
        <Button
          size="small"
          variant="outlined"
          disabled={domains.length === 0}
          onClick={() =>
            void onOpen(allOpen ? [] : domains.map(({ group }) => group))
          }
          sx={OUTLINED}
        >
          {allOpen
            ? t("assessment.full.collapseAll")
            : t("assessment.full.expandAll")}
        </Button>
        <Button
          size="small"
          variant="outlined"
          aria-pressed={fullText}
          onClick={() => void onFullText(!fullText)}
          sx={{
            ...OUTLINED,
            ...(fullText
              ? { bgcolor: "primary.main", color: "primary.contrastText" }
              : {}),
            "&:hover": fullText ? { bgcolor: "primary.main" } : {},
          }}
        >
          {t("assessment.full.fullText")}
        </Button>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={filter}
          aria-label={t("assessment.full.filterLabel")}
          onChange={(_, chosen: FullFilter | null) => {
            // An exclusive group reports null for a click on the chosen
            // button, which would leave nothing chosen.
            if (chosen !== null) {
              void onFilter(chosen);
            }
          }}
        >
          {filters.map((each) => (
            <ToggleButton
              key={each}
              value={each}
              sx={{
                textTransform: "none",
                py: 0.25,
                fontSize: "0.8125rem",
                fontWeight: 600,
                color: "text.primary",
                "&.Mui-selected, &.Mui-selected:hover": {
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                },
              }}
            >
              {t(`assessment.full.filter.${each}`)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Typography variant="body2" color="text.secondary" sx={{ ml: "auto" }}>
          {counts.join(" · ")}
        </Typography>
      </Stack>
      {domains.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t(`assessment.full.empty.${filter}`)}
        </Typography>
      ) : (
        domains.map((domain) => (
          <DomainCard
            key={domain.group}
            domain={domain}
            filter={filter}
            fullText={fullText}
            open={open.has(domain.group)}
            onToggle={() =>
              onOpen(
                open.has(domain.group)
                  ? [...open].filter((group) => group !== domain.group)
                  : [...open, domain.group],
              )
            }
            historyHrefOf={historyHrefOf}
            offersAllRatings={offersAllRatings}
            categorised={view.categorised}
          />
        ))
      )}
    </Stack>
  );
};
